import React from 'react';
import { Box, Stack, Tooltip } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { scroller } from 'react-scroll';

import { FormQuestion } from '../../../../../../../Utils/Types/FormBuilder';
import scrollToOptions from '../../../../../../../Utils/scrollToOptions';
import QuestionShow from './QuestionShow';
import TitleWhenAnswered from './TitleWhenAnswered';
import { submitForm, transformKeys } from './utils';
import useApiMutation from '../../../../../../../Hooks/useApiMutation';
import useApiQuery from '../../../../../../../Hooks/useApiQuery';
import queriesConfig from '../../../../../../../Services/queriesConfig';
import { groupByQuestionId } from '../../../../../../../Utils/Posts/Form/formBuilder';
import SectionShow from './SectionShow';
import ConditionalWrapper from '../../../../../../ConditionalWrapper';
import FormSectionWizard from '../../../../../../FormSectionWizard';
import { findIndex } from 'lodash';
import { LoadingButton } from '@mui/lab';

export default function FormShow({ lang, post, submissionOngoing, isAnswered }: any) {
  const queryClient = useQueryClient();
  const { form_questions: questions } = post;
  const [disabledForm, setDisabledForm] = React.useState(false);
  const [errors, setErrors] = React.useState<any>({});
  const [loading, setLoading] = React.useState(false);
  const [showResponse, setShowResponse] = React.useState(isAnswered ? false : true);

  const form = useForm({
    defaultValues: {
      answers_attributes: [],
    },
  });

  const { data: formResponse } = useApiQuery({
    query: queriesConfig.getFormResponse(post.id, post.meta?.user_form_submission_id),
    options: {
      enabled: isAnswered && questions.length > 0,
    },
    onSuccess: (data: { answers: any[] }) => {
      const defaultAnswersAttributes: any[] = questions.map((question: FormQuestion) =>
        data.answers.find((answer: any) => answer.question_id === question.id),
      );

      form.reset({ answers_attributes: defaultAnswersAttributes });
    },
  });

  const endpoint = isAnswered
    ? queriesConfig.updateFormResponse(post.id, post.meta?.user_form_submission_id)
    : queriesConfig.createFormResponse(post.id);

  const { mutate } = useApiMutation({
    query: endpoint,
    onSuccess: () => {
      setLoading(false);
      setErrors({});
      setShowResponse(false);

      // Refresh the post to get the new has_answered
      queryClient.invalidateQueries(queriesConfig.getPost(post.id).queryKey);

      scroller.scrollTo('study-content-tab', { ...scrollToOptions, duration: 300 });
    },
    onError: (responseData: any) => {
      const formattedErrors = transformKeys(responseData);

      setErrors(formattedErrors);
      setLoading(false);
    },
  });

  const groupedSections = groupByQuestionId(questions);
  const hasSections = groupedSections.length > 0;

  return (
    <>
      <TitleWhenAnswered
        isAnswered={isAnswered}
        createdAt={formResponse?.created_at}
        submissionOngoing={submissionOngoing}
        showResponse={showResponse}
        setShowResponse={setShowResponse}
      />

      {showResponse && (
        <form
          id="post-form-response"
          onSubmit={form.handleSubmit((data) =>
            submitForm({ data, setLoading, submitFormResponse: mutate, questions }),
          )}
        >
          <Stack spacing={3}>
            <ConditionalWrapper
              condition={hasSections}
              wrapper={(children) => (
                <FormSectionWizard
                  post={post}
                  form={form}
                  loading={loading}
                  setErrors={setErrors}
                  groupedSections={groupedSections}
                >
                  {children}
                </FormSectionWizard>
              )}
            >
              {groupedSections.map((group: any, index: number) => (
                <React.Fragment key={index}>
                  <SectionShow question={group.section}>
                    {group.questions.map((question: FormQuestion, subIndex: number) => (
                      <QuestionShow
                        key={`${question.uuid || question.id}-${subIndex}`}
                        lang={lang}
                        question={question}
                        questions={questions}
                        errors={errors}
                        setError={setErrors}
                        setDisabledForm={setDisabledForm}
                        index={findIndex(questions, { id: question.id })}
                        errorIndex={
                          hasSections ? subIndex : findIndex(questions, { id: question.id })
                        }
                        disabled={!submissionOngoing}
                        form={form}
                      />
                    ))}
                  </SectionShow>
                </React.Fragment>
              ))}
            </ConditionalWrapper>

            {!hasSections && (
              <Box sx={{ mt: 3 }}>
                <Tooltip
                  title={
                    !submissionOngoing && (
                      <FormattedMessage id="FORM_RESPONSES.SUBMISSION_NOT_ONGOING" />
                    )
                  }
                  arrow
                >
                  <span>
                    <LoadingButton
                      variant="contained"
                      color="primary"
                      size="large"
                      type="submit"
                      loading={loading}
                      disabled={disabledForm || !submissionOngoing}
                    >
                      <FormattedMessage id="POST_FORM.FORM_BUILDER.SUBMIT_FORM" />
                    </LoadingButton>
                  </span>
                </Tooltip>
              </Box>
            )}
          </Stack>
        </form>
      )}
    </>
  );
}
